import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  DUE_FOR_DELETION_KEY,
  fetchDueForDeletion,
  type DueForDeletionItem,
} from '../api/dueForDeletion'
import { EmptyState } from '../components/EmptyState'
import { ListDisclosure } from '../components/ListDisclosure'

const QUEUE_LABELS: Record<DueForDeletionItem['queue'], string> = {
  coordination: 'Emergency coordination',
  old_flow: 'Emergency (old flow)',
}

const DATE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

function day(iso: string): string {
  return DATE.format(new Date(iso))
}

/**
 * Due for deletion (M37-T02, owner ruling 2026-10-24). Every emergency record
 * whose personal details the 90-day deletion clears in the next two weeks.
 *
 * FOR LOOKING, NOT APPROVING. The deletion happens on the date whether or not
 * anyone opens this page, and nothing here can stop it: the owner ruled out a
 * Keep button, because personal data cannot be kept for marketing without the
 * person's consent. The page says so, so nobody waits on it.
 */
export function DueForDeletionPage(): ReactNode {
  const { token } = useAuth()
  const query = useQuery({
    queryKey: DUE_FOR_DELETION_KEY,
    queryFn: () => fetchDueForDeletion(token),
    enabled: Boolean(token),
  })
  const items = query.data?.data ?? []
  const days = query.data?.window_days ?? 14

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Due for deletion
        </h1>
        <p className="mt-2 text-sm text-ink-2">
          Emergency records whose personal details are deleted in the next{' '}
          {days} days. Names, phone numbers, places and notes go; the reference,
          dates, hospital and status stay, for counts.
        </p>
        <p
          role="note"
          className="mt-2 rounded-[10px] border border-line-2 bg-bed-warn-bg px-3 py-2 text-sm text-ink-1"
        >
          <span className="font-semibold">
            These deletions happen automatically, on the date shown.
          </span>{' '}
          That is 90 days after each request; records from the old queue that
          were already older were given one week&rsquo;s notice. Nothing on this
          page can stop a deletion. Medcor cannot keep personal details for
          marketing without the person&rsquo;s consent to marketing, and
          agreeing to be contacted later about the service is not that consent.
        </p>
      </div>

      {query.isPending ? (
        <div
          role="status"
          aria-label="Loading records due for deletion"
          className="h-40 w-full max-w-3xl animate-pulse rounded-2xl bg-bg-sunken"
        />
      ) : query.isError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-[10px] border border-bed-bad/40 bg-bed-bad-bg px-4 py-3"
        >
          <p className="text-sm font-medium text-bed-bad">
            {query.error instanceof ApiError
              ? query.error.message
              : 'Could not load the list.'}
          </p>
          <button
            type="button"
            onClick={() => query.refetch()}
            className="inline-flex h-8 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft"
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-line-1">
          <EmptyState title="Nothing is due for deletion.">
            No emergency record&rsquo;s details are deleted in the next {days}{' '}
            days.
          </EmptyState>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <ListDisclosure shown={items.length} total={query.data?.total} />
          <div className="overflow-x-auto rounded-2xl border border-line-1">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">
                Records due for deletion, soonest first
              </caption>
              <thead className="bg-bg-soft text-xs text-ink-3">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Deleted from
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Reference
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Queue
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Requested
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Hospital
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Agreed to later contact
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={`${item.queue}-${item.reference}`}
                    className="border-t border-line-1 text-ink-1"
                  >
                    <td className="px-3 py-2 font-medium">
                      {day(item.purge_after)}
                    </td>
                    <td className="px-3 py-2 font-mono">{item.reference}</td>
                    <td className="px-3 py-2">{QUEUE_LABELS[item.queue]}</td>
                    <td className="px-3 py-2">{day(item.created_at)}</td>
                    <td className="px-3 py-2">{item.hospital_name ?? '—'}</td>
                    <td className="px-3 py-2">
                      {item.status.replace(/_/g, ' ')}
                    </td>
                    <td className="px-3 py-2">
                      {item.contact_consent ? 'Yes' : 'No'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  )
}
